import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import {
  Play,
  ShoppingBag,
  Radio,
  Globe2,
  Sparkles,
  Trophy,
  Users,
  Briefcase,
  Mail,
  ArrowRight,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FeaturedCreatorsStrip from "@/components/plyx/FeaturedCreatorsStrip";

/**
 * Plyx — coming-soon landing page.
 * Lives at /plyx (becomes plyx.com once the domain is purchased).
 *
 * Design DNA — intentionally NOT AfriMarket's palette:
 *   - Jet black background (#0A0A0A)
 *   - Electric magenta primary (#E91E63)
 *   - Cyan accent (#00E5FF)
 *   - Inter / Manrope typography (matches AfriMarket so the design system stays unified)
 *
 * Goal: collect waitlist emails + show real founder count. Nothing else.
 */
const PlyxLogo = ({ size = 48 }) => (
  <div className="inline-flex items-center gap-2" data-testid="plyx-logo">
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-label="Plyx">
      <defs>
        <linearGradient id="plyxGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E91E63" />
          <stop offset="1" stopColor="#00E5FF" />
        </linearGradient>
      </defs>
      {/* Play triangle that morphs into an X via the slash */}
      <polygon points="14,10 14,54 52,32" fill="url(#plyxGrad)" />
      <line x1="10" y1="54" x2="56" y2="10" stroke="#0A0A0A" strokeWidth="6" strokeLinecap="round" />
    </svg>
    <span
      className="font-display text-3xl font-bold tracking-tight text-white"
      style={{ letterSpacing: "-0.04em" }}
    >
      Plyx
    </span>
  </div>
);

const PILLARS = [
  { icon: Play, title: "Short Video Feed", body: "TikTok-style vertical scroll, supercharged with culture-aware AI recommendations." },
  { icon: Radio, title: "Live Streaming", body: "Creators + businesses go live. Multi-guest streams, live battles, real-time shopping." },
  { icon: ShoppingBag, title: "Shoppable Everything", body: "Every video is a storefront. Verified sellers, escrow, multi-currency built in." },
  { icon: Trophy, title: "Creator Monetisation", body: "Gifts, coins, subscriptions, affiliate kickbacks, watch-to-earn — paid out in your local currency." },
  { icon: Users, title: "Community Hub", body: "Profiles, groups, voice + video calls, interest-based circles, events." },
  { icon: Sparkles, title: "AI Native", body: "Auto-dubs your videos to 20+ languages. Generates captions, edits, moderates. True global reach." },
  { icon: Briefcase, title: "Diaspora Hub", body: "Jobs, housing, immigration resources, business directory. One per diaspora community, anywhere on Earth." },
  { icon: Globe2, title: "Cross-Border Wallet", body: "One wallet across 60+ countries. Send, receive, withdraw — remittance built in." },
];

// "Why Plyx is bigger than TikTok" — the original 10 killer extras the user
// explicitly asked NOT to remove. These are the moat — every one of them is
// something TikTok can't copy without becoming a different company.
const KILLER_EXTRAS = [
  {
    emoji: "💸",
    title: "Built-in Remittance",
    body: "Aunty in Lagos sells Ankara through her video → cousin in London buys → Aunty receives in Naira via Stripe Connect. The first video platform that doubles as a remittance channel.",
  },
  {
    emoji: "🌍",
    title: "Geo-Feeds for Every Diaspora",
    body: "Switch between Lagos · London · Toronto · Mumbai · Mexico City · Kingston · Manila — see what's trending in any community, in any corner of the world.",
  },
  {
    emoji: "🗣️",
    title: "AI Auto-Dubbing in 20+ Languages",
    body: "Post once in English — Plyx instantly dubs to Yoruba, Spanish, Mandarin, Hindi, French, Portuguese, Arabic, Tagalog, and more. Voice-matched, lip-synced.",
  },
  {
    emoji: "🎁",
    title: "Watch-to-Earn Credit",
    body: "Scroll Plyx → earn credit you can spend in the marketplace. Funded by sellers; capped to stop abuse. A retention mechanic no other platform can copy without owning commerce.",
  },
  {
    emoji: "🎉",
    title: "Cultural Calendar Mode",
    body: "Eid · Sallah · Diwali · Lunar New Year · Notting Hill Carnival · Día de los Muertos · Afronation — discover videos, products, and people around every event that matters to you.",
  },
  {
    emoji: "🎙️",
    title: "Live Audio Rooms",
    body: "Drop into Lagos street-food chats, Manila OFW circles, Punjabi auntie debates. Live audio with product drops directly inside the conversation.",
  },
  {
    emoji: "🤝",
    title: "Native Multi-Creator Splits",
    body: "Two creators collab → revenue splits automatically. No paperwork, no DMs, no waiting. Powered by Stripe Connect from day one.",
  },
  {
    emoji: "🌙",
    title: "24/7 AI Companion Feed",
    body: "At 3am with nothing fresh, our AI generates a personalised story-feed just for you — pulled from your interests, your community, and your cultural calendar. The feed never goes cold.",
  },
];


const Counter = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setDisplay(Math.floor(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{display.toLocaleString()}</span>;
};

const PlyxLanding = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    document.title = "Plyx — Where moments become movements";
    document.body.style.backgroundColor = "#0A0A0A";
    return () => { document.body.style.backgroundColor = ""; };
  }, []);

  useEffect(() => {
    api.get("/plyx/waitlist/count")
      .then((r) => setTotal(r.data.total || 0))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/plyx/waitlist", {
        email: email.trim(),
        name: name.trim() || null,
        country: country.trim() || null,
      });
      setJoined(true);
      setPosition(data.position);
      setTotal(data.total);
      toast.success(`You're #${data.position} in line.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join waitlist");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#0A0A0A" }} data-testid="plyx-landing">
      {/* Backdrop — animated grid + radial glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-[150%] h-[150%] opacity-30"
             style={{
               background: "radial-gradient(ellipse at 30% 30%, #E91E63 0%, transparent 35%), radial-gradient(ellipse at 70% 70%, #00E5FF 0%, transparent 40%)",
               filter: "blur(80px)",
             }} />
        <div className="absolute inset-0 opacity-[0.04]"
             style={{
               backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
               backgroundSize: "48px 48px",
             }} />
      </div>

      {/* Top nav */}
      <nav className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <PlyxLogo size={36} />
        {!isPlyxStandaloneMode() && (
          <Link to="/" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="back-to-afrimarket">
            ← Back to AfriMarket
          </Link>
        )}
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 pt-12 lg:pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-xs uppercase tracking-[0.2em] text-white/70 mb-8" data-testid="plyx-status-pill">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
            Coming soon · 2026
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold leading-[1.02] tracking-tight" data-testid="plyx-hero-headline">
            Where moments<br/>
            <span className="bg-gradient-to-r from-[#E91E63] via-[#FF4081] to-[#00E5FF] bg-clip-text text-transparent">become movements.</span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed" data-testid="plyx-hero-sub">
            {isPlyxStandaloneMode()
              ? "The world's social-commerce super-app — watch, sell, earn, connect across every culture. Launching 2026."
              : "The world's social-commerce super-app — watch, sell, earn, connect across every culture. Launching with the African Diaspora Hub, then opening to every community on Earth."}
          </p>

          {/* Live preview CTA — jumps stakeholders straight into the working product */}
          <div className="mt-6">
            <Link
              to="/plyx/feed"
              data-testid="plyx-live-preview-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#0A0A0A] hover:scale-[1.02] transition-transform"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              <Play className="w-4 h-4 fill-current"/>
              Preview live now — open the feed
              <ArrowRight className="w-4 h-4"/>
            </Link>
            <p className="mt-2 text-[11px] text-white/40">
              {isPlyxStandaloneMode()
                ? "Beta is live. Sign up below to lock in your founder slot for the public launch."
                : "Beta is open inside AfriMarket today. Sign up below to lock in your founder slot for the standalone launch."}
            </p>
          </div>

          {/* Featured creators — spotlight 3–5 hand-picked or top-engagement creators */}
          <FeaturedCreatorsStrip limit={5} />

          {/* Waitlist form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 max-w-xl"
          >
            {joined ? (
              <div className="rounded-2xl bg-white/5 border border-[#00E5FF]/40 p-6" data-testid="plyx-joined-card">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="w-6 h-6 text-[#00E5FF]" />
                  <span className="font-display text-xl">You&apos;re in.</span>
                </div>
                <p className="text-white/70 text-sm">
                  You&apos;re position <span className="font-bold text-white">#<Counter value={position || 0} /></span> on the Plyx founder list.
                  We&apos;ll email you the moment beta opens. Share your invite link to skip the queue.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="plyx-waitlist-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }}
                    className="w-full px-4 py-3.5 rounded-xl border border-white/15 placeholder-white/40 focus:outline-none focus:border-[#E91E63] transition-colors"
                    data-testid="plyx-name-input"
                  />
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Country (e.g. UK)"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }}
                    className="w-full px-4 py-3.5 rounded-xl border border-white/15 placeholder-white/40 focus:outline-none focus:border-[#E91E63] transition-colors"
                    data-testid="plyx-country-input"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }}
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-white/15 placeholder-white/40 focus:outline-none focus:border-[#E91E63] transition-colors"
                      data-testid="plyx-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#E91E63] to-[#FF4081] text-white font-semibold inline-flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 shadow-[0_8px_30px_-4px_rgba(233,30,99,0.5)]"
                    data-testid="plyx-join-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Join <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
                <p className="text-xs text-white/40">No spam. One launch email. Unsubscribe anytime.</p>
              </form>
            )}

            <div className="mt-8 flex items-center gap-3 text-white/60 text-sm">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-7 h-7 rounded-full border-2 border-[#0A0A0A]"
                       style={{
                         background: `linear-gradient(${i * 90}deg, #E91E63, #00E5FF)`,
                       }} />
                ))}
              </div>
              <span data-testid="plyx-founder-count">
                <Counter value={total} /> founders already waiting.
              </span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Feature pillars */}
      <section className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-20 border-t border-white/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <div className="text-[#E91E63] text-xs uppercase tracking-[0.3em] mb-3">What you&apos;re getting</div>
          <h2 className="font-display text-3xl sm:text-5xl font-bold max-w-3xl leading-tight">
            Eight reasons Plyx<br/>
            <span className="bg-gradient-to-r from-[#E91E63] to-[#00E5FF] bg-clip-text text-transparent">isn&apos;t another TikTok clone.</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plyx-pillars">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="group p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#E91E63]/40 hover:bg-white/[0.05] transition-all"
                data-testid={`plyx-pillar-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E91E63]/20 to-[#00E5FF]/20 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="font-display text-lg font-semibold mb-2">{p.title}</div>
                <div className="text-sm text-white/60 leading-relaxed">{p.body}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* The 8 killer extras nobody else has — the moat */}
      <section className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-20 border-t border-white/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14 max-w-3xl"
        >
          <div className="text-[#00E5FF] text-xs uppercase tracking-[0.3em] mb-3">Why Plyx is bigger than TikTok</div>
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            Eight things TikTok<br/>
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#E91E63] bg-clip-text text-transparent">can never copy.</span>
          </h2>
          <p className="mt-5 text-white/60 text-base sm:text-lg leading-relaxed">
            These aren&apos;t features — they&apos;re structural advantages built on top of a real commerce engine. Anyone can clone a video feed. Nobody else has these.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4" data-testid="plyx-killer-extras">
          {KILLER_EXTRAS.map((k, i) => (
            <motion.div
              key={k.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
              className="group p-7 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/10 hover:border-[#E91E63]/40 transition-all"
              data-testid={`plyx-extra-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl shrink-0 leading-none">{k.emoji}</div>
                <div>
                  <div className="font-display text-xl font-semibold mb-2">{k.title}</div>
                  <div className="text-sm text-white/65 leading-relaxed">{k.body}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Diaspora Hub spotlight */}
      {/* African Diaspora launch-vertical section — hidden in standalone Plyx
          preview since it telegraphs the AfriMarket origin story. */}
      {!isPlyxStandaloneMode() && (
      <section className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-20">
        <div className="rounded-3xl p-10 lg:p-14 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, #E91E63 0%, #FF4081 50%, #00E5FF 100%)" }}>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }} />
          <div className="relative z-10 max-w-3xl">
            <div className="text-white/90 text-xs uppercase tracking-[0.3em] mb-4 font-semibold">The Launch Vertical · The African Diaspora Hub</div>
            <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 text-white">
              We&apos;re launching where we have unfair advantage — then opening to every diaspora on Earth.
            </h2>
            <p className="text-white/85 text-lg lg:text-xl max-w-2xl leading-relaxed">
              Day one: jobs, housing, immigration help, cultural events, and an African-owned business directory — built by, with, and for the African diaspora across the UK, US, Canada, and the continent itself. Then we replicate the same playbook for every other community that&apos;s been overlooked by mainstream platforms.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Jobs", "Housing", "Immigration", "Events", "Business Directory", "Cultural Communities"].map(t => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold text-white border border-white/30">
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-white/30">
              <div className="text-white/85 text-sm uppercase tracking-[0.2em] mb-3 font-semibold">Coming after Africa</div>
              <div className="flex flex-wrap gap-2">
                {["Caribbean", "Latinx", "South Asian", "Filipino", "Polish", "Vietnamese", "Brazilian", "+ every diaspora"].map(t => (
                  <span key={t} className="px-3 py-1.5 rounded-lg bg-black/20 backdrop-blur-sm text-sm font-medium text-white/95 border border-white/20">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* AfriMarket cross-link footer */}
      <footer className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-14 border-t border-white/5">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="text-white/50 text-sm">
            <PlyxLogo size={24} />
            <div className="mt-3 max-w-md">
              {isPlyxStandaloneMode()
                ? "Plyx — where moments become movements. Built for creators, by creators."
                : <>Built with <span className="text-[#E91E63]">♥</span> by the AfriMarket team. Plyx amplifies what AfriMarket started — and opens the door for every diaspora community on the planet.</>}
            </div>
          </div>
          <div className="flex items-center gap-6 text-white/70 text-sm">
            {!isPlyxStandaloneMode() && (
              <Link to="/" className="hover:text-white" data-testid="footer-afrimarket-link">afrimarket.org</Link>
            )}
            <a href={isPlyxStandaloneMode() ? "mailto:hello@plyx.co.uk" : "mailto:hello@afrimarket.org"} className="hover:text-white">{isPlyxStandaloneMode() ? "hello@plyx.co.uk" : "hello@plyx.app"}</a>
            <span className="text-white/30">© 2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlyxLanding;
